import axios, { AxiosError } from 'axios'
import { BASE_API_URL } from '@/configs'
import {
  AffiliateErrorCode,
  IAffiliate,
  IAffiliateApplyPayload,
  IAffiliateApplyResponse,
  IAffiliateLedgerEntry,
  IAffiliateLinkTarget,
  AffiliateLinkTargetType,
  IAffiliateRefLookup,
  IAffiliateUpdatePayload,
  IAffiliateWithdrawPayload,
  IAffiliateWithdrawResponse,
} from 'types/affiliates'
import { AuthBEAPIClient, BEAPIClient, handleResponseSuccess } from './BEClient'
import { PREFIXES } from './constants'

const AFFILIATES_BASE = `/${PREFIXES.GAME}/v1/affiliates`

const AFFILIATES_API_ENDPOINTS = {
  APPLY: `${AFFILIATES_BASE}/apply`,
  ME: `${AFFILIATES_BASE}/me`,
  LEDGER: `${AFFILIATES_BASE}/me/ledger`,
  WITHDRAW: `${AFFILIATES_BASE}/withdraw`,
  LINK_TARGETS: `${AFFILIATES_BASE}/me/link-targets`,
  REF_LOOKUP: (slug: string) => `${AFFILIATES_BASE}/ref/${encodeURIComponent(slug)}`,
}

const apply = async (payload: IAffiliateApplyPayload) => {
  const response = await AuthBEAPIClient.post<IAffiliateApplyResponse>(
    AFFILIATES_API_ENDPOINTS.APPLY,
    payload,
  )
  return handleResponseSuccess(response)
}

const getMe = async () => {
  const response = await AuthBEAPIClient.get<IAffiliate>(AFFILIATES_API_ENDPOINTS.ME)
  return handleResponseSuccess(response)
}

const getMeSSR = async (accessToken: string) => {
  const response = await axios.get<IAffiliate>(`${BASE_API_URL}${AFFILIATES_API_ENDPOINTS.ME}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  return handleResponseSuccess(response)
}

const updateMe = async (payload: IAffiliateUpdatePayload) => {
  const response = await AuthBEAPIClient.patch<IAffiliate>(AFFILIATES_API_ENDPOINTS.ME, payload)
  return handleResponseSuccess(response)
}

const getLedger = async (params?: { limit?: number; before_id?: number }) => {
  const response = await AuthBEAPIClient.get<IAffiliateLedgerEntry[]>(
    AFFILIATES_API_ENDPOINTS.LEDGER,
    { params },
  )
  return handleResponseSuccess(response)
}

const withdraw = async (payload: IAffiliateWithdrawPayload) => {
  const response = await AuthBEAPIClient.post<IAffiliateWithdrawResponse>(
    AFFILIATES_API_ENDPOINTS.WITHDRAW,
    payload,
  )
  return handleResponseSuccess(response)
}

const getLinkTargets = async (params: {
  partner_id: string
  type: AffiliateLinkTargetType
  q?: string
  limit?: number
}) => {
  const response = await AuthBEAPIClient.get<IAffiliateLinkTarget[]>(
    AFFILIATES_API_ENDPOINTS.LINK_TARGETS,
    { params },
  )
  return handleResponseSuccess(response)
}

const lookupRef = async (slug: string) => {
  const response = await BEAPIClient.get<IAffiliateRefLookup>(
    AFFILIATES_API_ENDPOINTS.REF_LOOKUP(slug),
  )
  return handleResponseSuccess(response)
}

/** True when the BE says the partner/slug doesn't exist or isn't approved (404). */
export const isAffiliateNotFound = (error: unknown): boolean =>
  (error as AxiosError)?.response?.status === 404

/** Returns the affiliate error code from a BE error (`detail` is a string for these). */
export const getAffiliateErrorCode = (error: unknown): AffiliateErrorCode | null => {
  const detail = (error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail
  return typeof detail === 'string' ? (detail as AffiliateErrorCode) : null
}

type FastApiValidationError = { loc: (string | number)[]; msg: string }

/** Maps a FastAPI 422 validation list to `{ field: message }` (last `loc` entry is the field). */
export const getAffiliateFieldErrors = (error: unknown): Record<string, string> => {
  const detail = (error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail
  if (!Array.isArray(detail)) return {}
  return (detail as FastApiValidationError[]).reduce<Record<string, string>>((acc, item) => {
    const field = item.loc?.[item.loc.length - 1]
    if (typeof field === 'string') acc[field] = item.msg
    return acc
  }, {})
}

export const affiliatesApi = {
  apply,
  getMe,
  getMeSSR,
  updateMe,
  getLedger,
  withdraw,
  getLinkTargets,
  lookupRef,
}
